import React from 'react';

import { useAppSelector } from '@/store';

import { AD_CARD_READS } from '../marketing-assets/ad-card-reads';

/**
 * Whether the active brand has any marketing asset yet (an image banner, HTML5 banner, video ad, mini-game or landing
 * page), for Home's "Top marketing assets" empty state: with none, its action is to create an asset first; with some,
 * to get one's tracking link. Reads the Marketing hub's own per-area lists (AD_CARD_READS), only while `enabled` (the
 * section is empty for lack of links), and answers as soon as any list has a row. `unknown` while reading, when not
 * enabled, or when a list could not be read and none had a row (the caller then keeps its tracking-link wording).
 */
export type HasAssets = 'unknown' | 'yes' | 'no';

export const useHasMarketingAssets = (enabled: boolean): HasAssets => {
  const tenantRaw = useAppSelector((state) => Number(state.casinoInstance.displayBrand?.tenant_id));
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const [answer, setAnswer] = React.useState<{ tenantId?: number; value: HasAssets }>({ value: 'unknown' });

  // Syncs with the network: the brand's asset lists, until one of them has a row.
  React.useEffect(() => {
    if (!enabled || !tenantId) return undefined;
    const ctrl = new AbortController();
    let found = false;
    const reads = Object.values(AD_CARD_READS).map((read) =>
      read(tenantId, ctrl.signal).then((rows) => {
        if (rows.length > 0 && !found && !ctrl.signal.aborted) {
          found = true;
          setAnswer({ tenantId, value: 'yes' });
        }
        return rows.length;
      }),
    );
    void Promise.allSettled(reads).then((results) => {
      if (ctrl.signal.aborted || found) return;
      const allRead = results.every((r) => r.status === 'fulfilled');
      setAnswer({ tenantId, value: allRead ? 'no' : 'unknown' });
    });
    return () => ctrl.abort();
  }, [enabled, tenantId]);

  return enabled && answer.tenantId === tenantId ? answer.value : 'unknown';
};

export default useHasMarketingAssets;
